import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import Box from "ds/components/Box";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useOpenTofuMigrate from "shared/Stack/useOpenTofuMigrate";
import { StackVendor } from "types/generated";

import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../../../constants";

const REFETCH_QUERIES = [
  ...REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
  "OpenTofuMigrationQueueSearch",
];

type StackSettingsOpenTofuMigrationConfirmationModalProps = {
  stackId: string;
};

const StackSettingsOpenTofuMigrationConfirmationModal = create(
  function StackSettingsOpenTofuMigrationConfirmationModal({
    stackId,
  }: StackSettingsOpenTofuMigrationConfirmationModalProps) {
    const modal = useModal();
    const { reportSuccess } = useTypedContext(FlashContext);

    const [migrate, { loading }] = useOpenTofuMigrate({
      refetchQueries: REFETCH_QUERIES,
    });

    const handleConfirm = useCallback(() => {
      migrate(
        {
          input: {
            first: 1,
            after: null,
            fullTextSearch: null,
            orderBy: null,
            requestedPage: null,
            predicates: [
              {
                field: "slug",
                exclude: null,
                constraint: {
                  booleanEquals: null,
                  enumEquals: null,
                  stringMatches: null,
                  hierarchyNode: null,
                  hierarchyNodeValueEquals: null,
                  timeInRange: null,
                  timeInLast: null,
                  stringEquals: [stackId],
                },
              },
            ],
          },
          target: StackVendor.OpenTofu,
          triggerRun: null,
        },
        (data) => {
          if (data?.openTofuMigrate) {
            reportSuccess({ message: "Stack vendor migration was successfully queued" });
            modal.hide();
          }
        }
      );
    }, [migrate, modal, reportSuccess, stackId]);

    return (
      <ConfirmationModal
        title="Confirm Migration"
        confirmationButtonLabel="Migrate"
        confirmationButtonVariant="primary"
        onConfirm={handleConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <Box direction="column" gap="medium">
          <ConfirmationModalMessage>
            This will change the stack vendor from Terraform to OpenTofu. The stack will use native
            OpenTofu support instead of running OpenTofu through the Terraform vendor. Your existing
            configuration and state will be preserved.
          </ConfirmationModalMessage>
          <ConfirmationModalMessage>
            The migration is queued and will be processed shortly. You will be able to roll the
            migration back.
          </ConfirmationModalMessage>
        </Box>
      </ConfirmationModal>
    );
  }
);

export const showStackSettingsOpenTofuMigrationConfirmationModal = (
  props: StackSettingsOpenTofuMigrationConfirmationModalProps
) => show(StackSettingsOpenTofuMigrationConfirmationModal, props);
